import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { X, Send, Save, Trash2, Edit, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const BrouillonActionModal = ({ isOpen, onClose, onAction, offer, userType = 'general' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    company: '',
    qualites_attendues: '',
    lieu_travail: '',
    date_arrivee: '',
    type_poste: '',
    taux_horaire: ''
  });

  // Determine if user is Manager HS (can edit all fields) or Manager General (restricted fields)
  const isManagerHS = userType === 'horizon_solution';
  const isManagerGeneral = userType === 'general';

  useEffect(() => {
    if (offer) {
      setFormData({
        title: offer.title || '',
        description: offer.description || '',
        company: offer.company || '',
        qualites_attendues: offer.qualites_attendues || '',
        lieu_travail: offer.lieu_travail || '',
        date_arrivee: offer.date_arrivee ? offer.date_arrivee.split('T')[0] : '',
        type_poste: offer.type_poste || '',
        taux_horaire: offer.taux_horaire ? offer.taux_horaire.toString() : ''
      });
    }
  }, [offer]);

  if (!isOpen || !offer) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleBackToView = () => {
    // Reset form data to original values
    setFormData({
      title: offer.title || '',
      description: offer.description || '',
      company: offer.company || '',
      qualites_attendues: offer.qualites_attendues || '',
      lieu_travail: offer.lieu_travail || '',
      date_arrivee: offer.date_arrivee ? offer.date_arrivee.split('T')[0] : '',
      type_poste: offer.type_poste || '',
      taux_horaire: offer.taux_horaire ? offer.taux_horaire.toString() : ''
    });
    setIsEditing(false);
  };

  const handleSaveChanges = async () => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La description est requise');
      return;
    }
    // Company is only required for Manager HS
    if (isManagerHS && !formData.company) {
      toast.error('La société est requise');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data for API
      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        company: formData.company,
        qualites_attendues: formData.qualites_attendues.trim(),
        lieu_travail: formData.lieu_travail.trim(),
        date_arrivee: formData.date_arrivee || null,
        type_poste: formData.type_poste || null,
        taux_horaire: formData.taux_horaire ? parseFloat(formData.taux_horaire) : null
      };

      const response = await fetch(`${API}/job-offers/${offer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (response.ok) {
        toast.success('Brouillon mis à jour avec succès');
        setIsEditing(false);
        // Update offer data
        Object.assign(offer, updateData);
        onAction('updated'); // Notify parent to refresh data
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating offer:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/app_uploads/ma_conciergerie.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/app_uploads/mon_evenementiel.png',
    'SISENS': 'https://customer-assets.emergentagent.com/app_uploads/sisens.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/app_uploads/prysme.png'
  };

  const companies = ['Ma Conciergerie', 'Mon Evenementiel', 'SISENS', 'Horizon Solution', 'Prysme'];
  const typePosteOptions = ['CDI', 'CDD', 'Stage', 'Freelance', 'Alternance'];

  return (
    <div className="modal-overlay">
      <div className="modal-container" style={{ maxWidth: isEditing ? '800px' : '600px' }}>
        <Card className="modal-card">
          <CardHeader className="modal-header">
            <div className="flex justify-between items-center">
              <CardTitle className="modal-title">
                {isEditing ? (
                  <>
                    <Edit size={20} className="mr-2" />
                    Modifier le brouillon
                  </>
                ) : (
                  <>
                    <Edit size={20} className="mr-2" />
                    Gestion du brouillon
                  </>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToView}
                    className="text-gray-600"
                  >
                    <ArrowLeft size={16} className="mr-1" />
                    Retour
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="modal-close"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="modal-content">
            {isEditing ? (
              /* Edit Form */
              <div className="form-container">
                {/* Title */}
                <div className="form-group">
                  <Label htmlFor="title" className="form-label">
                    Titre du poste *
                  </Label>
                  <Input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="form-input"
                    placeholder="Ex: Développeur Full Stack"
                  />
                </div>

                {/* Company - Only for Manager HS */}
                {isManagerHS && (
                  <div className="form-group">
                    <Label htmlFor="company" className="form-label">
                      Société *
                    </Label>
                    <Select value={formData.company} onValueChange={(value) => handleInputChange('company', value)}>
                      <SelectTrigger className="form-input">
                        <SelectValue placeholder="Sélectionnez une société" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company} value={company}>
                            <div className="flex items-center">
                              <img
                                src={companyLogos[company]}
                                alt={`Logo ${company}`}
                                className="w-5 h-5 mr-2"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              {company}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Description */}
                <div className="form-group">
                  <Label htmlFor="description" className="form-label">
                    Description du poste *
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="form-textarea"
                    rows={4}
                    placeholder="Décrivez le poste en détail..."
                  />
                </div>

                {/* Qualités attendues */}
                <div className="form-group">
                  <Label htmlFor="qualites_attendues" className="form-label">
                    Qualités attendues
                  </Label>
                  <Textarea
                    id="qualites_attendues"
                    value={formData.qualites_attendues}
                    onChange={(e) => handleInputChange('qualites_attendues', e.target.value)}
                    className="form-textarea"
                    rows={3}
                    placeholder="Qualités et compétences recherchées..."
                  />
                </div>

                {/* Lieu de travail et Date */}
                <div className="form-row">
                  <div className="form-group">
                    <Label htmlFor="lieu_travail" className="form-label">
                      Lieu de travail
                    </Label>
                    <Input
                      id="lieu_travail"
                      type="text"
                      value={formData.lieu_travail}
                      onChange={(e) => handleInputChange('lieu_travail', e.target.value)}
                      className="form-input"
                      placeholder="Ex: Paris, Remote..."
                    />
                  </div>
                  <div className="form-group">
                    <Label htmlFor="date_arrivee" className="form-label">
                      Date d'arrivée idéale
                    </Label>
                    <Input
                      id="date_arrivee"
                      type="date"
                      value={formData.date_arrivee}
                      onChange={(e) => handleInputChange('date_arrivee', e.target.value)}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Type de poste et Taux horaire - Only for Manager HS */}
                {isManagerHS && (
                  <div className="form-row">
                    <div className="form-group">
                      <Label htmlFor="type_poste" className="form-label">
                        Type de poste
                      </Label>
                      <Select value={formData.type_poste} onValueChange={(value) => handleInputChange('type_poste', value)}>
                        <SelectTrigger className="form-input">
                          <SelectValue placeholder="Sélectionnez un type" />
                        </SelectTrigger>
                        <SelectContent>
                          {typePosteOptions.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-group">
                      <Label htmlFor="taux_horaire" className="form-label">
                        Taux horaire (€/h)
                      </Label>
                      <Input
                        id="taux_horaire"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.taux_horaire}
                        onChange={(e) => handleInputChange('taux_horaire', e.target.value)}
                        className="form-input"
                        placeholder="Ex: 25.00"
                      />
                    </div>
                  </div>
                )}

                {/* Save/Cancel buttons for edit mode */}
                <div className="modal-actions">
                  <Button
                    onClick={handleSaveChanges}
                    disabled={loading}
                    className="btn-primary"
                    style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                  >
                    <Save size={16} className="mr-2" />
                    {loading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
                  </Button>
                  <Button
                    onClick={handleBackToView}
                    disabled={loading}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                {/* Offer Details */}
                <div className="offer-summary">
                  <h3 className="text-lg font-semibold mb-3">{offer.title}</h3>
                  <div className="offer-info-grid">
                    <div className="offer-info-item">
                      <span className="font-medium">Entreprise :</span>
                      <span>{offer.company}</span>
                    </div>
                    <div className="offer-info-item">
                      <span className="font-medium">Date idéale :</span>
                      <span>{formatDate(offer.date_arrivee)}</span>
                    </div>
                    <div className="offer-info-item">
                      <span className="font-medium">Lieu :</span>
                      <span>{offer.lieu_travail || 'Non spécifié'}</span>
                    </div>
                    {offer.type_poste && (
                      <div className="offer-info-item">
                        <span className="font-medium">Type :</span>
                        <span>{offer.type_poste}</span>
                      </div>
                    )}
                    {offer.taux_horaire && (
                      <div className="offer-info-item">
                        <span className="font-medium">Taux horaire :</span>
                        <span>{offer.taux_horaire}€/h</span>
                      </div>
                    )}
                    <div className="offer-info-item">
                      <span className="font-medium">Créé le :</span>
                      <span>{formatDate(offer.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="offer-description mt-4">
                    <h4 className="font-medium mb-2">Description :</h4>
                    <p className="text-gray-700 text-sm">{offer.description}</p>
                  </div>

                  {offer.qualites_attendues && (
                    <div className="offer-description mt-4">
                      <h4 className="font-medium mb-2">Qualités attendues :</h4>
                      <p className="text-gray-700 text-sm">{offer.qualites_attendues}</p>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="brouillon-actions">
                  <Button
                    onClick={() => onAction('envoyer')}
                    className="btn-success"
                    style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                  >
                    <Send size={16} className="mr-2" />
                    Envoyer
                  </Button>
                  <Button
                    onClick={handleEditClick}
                    className="btn-primary"
                    style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
                  >
                    <Edit size={16} className="mr-2" />
                    Modifier
                  </Button>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="btn-cancel"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => onAction('supprimer')}
                    className="btn-danger"
                    style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Supprimer
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BrouillonActionModal;